import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle, Zap, ArrowRight, Mail, CreditCard, AlertCircle, Star, TrendingUp, Users, Shield, FileText } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { onboardingService } from '@/services/onboardingService';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '@/config';
import { Alert, AlertDescription } from '@/components/ui/alert';

// Testimonials based on audience
const getTestimonials = (audience: string) => {
  const lowerAudience = audience.toLowerCase();
  
  if (lowerAudience.includes('realtor') || lowerAudience.includes('real estate')) {
    return [
      {
        name: "Sarah Chen",
        role: "Top Agent, Keller Williams",
        content: "Booked $2.4M in listings from one month of campaigns. 10X ROI on first deal alone.",
        rating: 5
      },
      {
        name: "Mike Rodriguez", 
        role: "Broker, ReMax Elite",
        content: "47% more seller leads than Zillow Premier Agent at 1/10th the cost.",
        rating: 5
      },
      {
        name: "Emma Thompson",
        role: "Luxury Realtor, Compass",
        content: "Finally reaching high-net-worth clients. Closed 3 waterfront properties from cold email.",
        rating: 5
      }
    ];
  } else if (lowerAudience.includes('saas') || lowerAudience.includes('founder')) {
    return [
      {
        name: "Sarah Chen",
        role: "Founder, TechStart",
        content: "Booked 12 enterprise demos in week 1. Better than our $15k/mo SDR agency.",
        rating: 5
      },
      {
        name: "Mike Rodriguez", 
        role: "CEO, ScaleUp (YC W22)",
        content: "Went from 0 to $50k MRR using only Munger. Every founder needs this.",
        rating: 5
      },
      {
        name: "Emma Thompson",
        role: "VP Sales, Series-A SaaS",
        content: "Replaced 3 SDRs, improved meeting quality, saved $288k/year.",
        rating: 5
      }
    ];
  } else {
    return [
      {
        name: "Sarah Chen",
        role: "Founder, TechStart",
        content: "Booked 12 meetings in my first week. The AI writes better emails than I do!",
        rating: 5
      },
      {
        name: "Mike Rodriguez", 
        role: "VP Sales, ScaleUp",
        content: "Replaced 3 SDRs and increased our meeting rate by 4x. Incredible ROI.",
        rating: 5
      },
      {
        name: "Emma Thompson",
        role: "CEO, B2B SaaS",
        content: "Finally, cold email that doesn't feel cold. Our prospects love the personalization.",
        rating: 5
      }
    ];
  }
};

const OptimizedOnboardingV2 = ({ isOpen, onClose, initialPlan = 'growth', noFreeTrial = false }: { isOpen: boolean; onClose: () => void; initialPlan?: string; noFreeTrial?: boolean }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState(initialPlan);
  
  // Form data
  const [formData, setFormData] = useState({
    // Product info
    productName: '',
    productDescription: '',
    targetAudience: '',
    painPoints: '',
    callToAction: 'Book a demo',
    // User info
    email: '',
    userName: '',
    password: '',
    // Campaign preview data
    estimatedReach: 0,
    sampleSubjectLines: [] as string[],
    targetCompanies: [] as string[]
  });

  // Generate campaign preview when product info changes
  useEffect(() => {
    if (formData.productName && formData.targetAudience) {
      // Generate better subject lines based on audience
      let subjectLines = [];
      let companies = [];
      let reach = 15000;
      
      const audience = formData.targetAudience.toLowerCase();
      
      if (audience.includes('realtor') || audience.includes('real estate')) {
        subjectLines = [
          `${formData.productName} helped ReMax close 47% more deals`,
          `Why top realtors switched to ${formData.productName}`,
          `Sarah from Keller Williams 3x'd listings with this`
        ];
        companies = ['ReMax', 'Keller Williams', 'Century 21', 'Coldwell Banker', 'Compass'];
        reach = 64000; // 80% of ~80k realtors
      } else if (audience.includes('saas') || audience.includes('founder')) {
        subjectLines = [
          `How Stripe uses ${formData.productName} to scale`,
          `${formData.productName} - YC founders' secret weapon`,
          `Noticed your Series A - perfect timing for ${formData.productName}`
        ];
        companies = ['Stripe', 'Notion', 'Linear', 'Vercel', 'Supabase'];
      } else if (audience.includes('agency') || audience.includes('marketing')) {
        subjectLines = [
          `How Ogilvy automated client acquisition`,
          `${formData.productName} = 10x more clients, same team`,
          `Your competitors are using this to steal clients`
        ];
        companies = ['Ogilvy', 'BBDO', 'McCann', 'Publicis', 'WPP'];
      } else {
        subjectLines = [
          `${formData.productName} helped competitors 2x revenue`,
          `Quick question about scaling ${audience}`,
          `How ${audience} book 50+ meetings/month`
        ];
        companies = ['Fortune 500', 'Inc 5000', 'Tech Giants', 'Industry Leaders', 'Market Leaders'];
      }
      
      setFormData(prev => ({
        ...prev,
        estimatedReach: reach,
        sampleSubjectLines: subjectLines,
        targetCompanies: companies
      }));
    }
  }, [formData.productName, formData.targetAudience]);

  // Plans configuration (Whop)
  const plans = {
    growth: {
      name: 'Autonomous AI Marketer',
      price: 97,
      emailsPerMonth: '150,000',
      features: noFreeTrial ? [
        'AI sets up everything automatically',
        'Finds prospects from 250M+ database',
        'Writes & sends personalized emails',
        'Self‑optimizes based on results',
        'Tracks conversions with pixels'
      ] : [
        'AI sets up everything automatically',
        'Writes & sends personalized emails',
        'Self‑optimizes based on results',
        'Tracks conversions with pixels',
        '3‑day free trial'
      ]
    },
    pro: {
      name: 'Pro AI Marketer',
      price: 297,
      emailsPerMonth: '500,000',
      features: [
        'Everything in Growth plan',
        'Priority AI learning',
        'Advanced analytics',
        'Dedicated support',
        'White‑label options'
      ]
    }
  };

  // Complete onboarding flow
  const handleSubmit = async () => {
    setIsProcessing(true);
    setError(null);

    try {
      // Track InitiateCheckout event for Facebook Pixel
      if (typeof window !== 'undefined' && (window as unknown as { fbq?: (...args: unknown[]) => void }).fbq) {
        (window as unknown as { fbq: (...args: unknown[]) => void }).fbq('track', 'InitiateCheckout', {
          value: plans[selectedPlan as keyof typeof plans].price,
          currency: 'USD',
          content_name: plans[selectedPlan as keyof typeof plans].name,
          content_category: 'subscription',
          content_ids: [selectedPlan],
          num_items: 1
        });
        console.log('Facebook Pixel: Tracked InitiateCheckout event');
      }

      // Track Google Analytics event
      if (typeof window !== 'undefined' && (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag) {
        (window as unknown as { gtag: (...args: unknown[]) => void }).gtag('event', 'begin_checkout', {
          value: plans[selectedPlan as keyof typeof plans].price,
          currency: 'USD',
          items: [{
            id: selectedPlan,
            name: plans[selectedPlan as keyof typeof plans].name,
            category: 'subscription',
            quantity: 1,
            price: plans[selectedPlan as keyof typeof plans].price
          }]
        });
        console.log('Google Analytics: Tracked begin_checkout event');
      }

      // Create account and redirect to Whop checkout
      const result = await onboardingService.simpleSignup({
        userName: formData.userName,
        email: formData.email,
        password: formData.password,
        planId: selectedPlan,
        noFreeTrial: noFreeTrial
      });

      if (result.redirecting) {
        // Show loading state while redirecting
        return;
      }
    } catch (err: unknown) {
      console.error('Onboarding error:', err);
      
      // Track error event
      if (typeof window !== 'undefined' && (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag) {
        const e = err as { message?: string };
        (window as unknown as { gtag: (...args: unknown[]) => void }).gtag('event', 'onboarding_error', {
          error_message: e.message || 'Unknown error',
          email_provided: !!formData.email,
          product_provided: !!formData.productName
        });
      }
      
      const e = err as { message?: string };
      setError(e.message || 'Something went wrong. Please try again or contact support.');
      setIsProcessing(false);
    }
  };

  const canProceed = () => {
    return formData.productName && formData.email;
  };

  const renderContent = () => {
    const currentPlan = plans[selectedPlan as keyof typeof plans];
    
    return (
      <div className="grid md:grid-cols-2 gap-8">
        {/* Left side - Form */}
        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-semibold mb-4">Tell Us What You Sell</h3>
            <div className="space-y-4">
              {/* Product Info */}
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900">1. Tell us about your product</h4>
                
                <Input
                  placeholder="Product name"
                  value={formData.productName}
                  onChange={(e) => setFormData(prev => ({ ...prev, productName: e.target.value }))}
                />
                
                <Textarea
                  placeholder="What does your product do?"
                  value={formData.productDescription}
                  onChange={(e) => setFormData(prev => ({ ...prev, productDescription: e.target.value }))}
                  className="min-h-[60px]"
                />
                
                <Input
                  placeholder="Target audience (e.g., B2B SaaS founders)"
                  value={formData.targetAudience}
                  onChange={(e) => setFormData(prev => ({ ...prev, targetAudience: e.target.value }))}
                />
                
                <Select
                  value={formData.callToAction}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, callToAction: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Book a demo">Book a demo</SelectItem>
                    <SelectItem value="Start free trial">Start free trial</SelectItem>
                    <SelectItem value="Get started">Get started</SelectItem>
                    <SelectItem value="Schedule a call">Schedule a call</SelectItem>
                    <SelectItem value="Sign up free">Sign up free</SelectItem>
                    <SelectItem value="Get pricing">Get pricing</SelectItem>
                    <SelectItem value="Learn more">Learn more</SelectItem>
                    <SelectItem value="Request access">Request access</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Account Info */}
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900">2. Create your account</h4>
                
                <Input
                  type="email"
                  placeholder="Email address"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                />
                
                <Input
                  placeholder="Your name (optional)"
                  value={formData.userName}
                  onChange={(e) => setFormData(prev => ({ ...prev, userName: e.target.value }))}
                />
                
                <Input
                  type="password"
                  placeholder="Password (min 6 characters)"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                />
              </div>

              {/* Plan Selection */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">3. Select your plan</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div 
                    className={`p-4 rounded-lg cursor-pointer border-2 transition-all ${
                      selectedPlan === 'growth' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedPlan('growth')}
                  >
                    <div className="font-medium">Growth Plan</div>
                    <div className="text-2xl font-bold mb-1">$97/mo</div>
                    <div className="text-sm text-gray-600">12+ meetings/week</div>
                  </div>
                  
                  <div 
                    className={`p-4 rounded-lg cursor-pointer border-2 transition-all ${
                      selectedPlan === 'pro' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedPlan('pro')}
                  >
                    <div className="font-medium">Pro Plan</div>
                    <div className="text-2xl font-bold mb-1">$297/mo</div>
                    <div className="text-sm text-gray-600">30+ meetings/week</div>
                  </div>
                </div>
                
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">{currentPlan.name}</span>
                    <span className="text-2xl font-bold">${currentPlan.price}/mo</span>
                  </div>
                  <ul className="space-y-1 text-sm text-gray-700">
                    {currentPlan.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <Button
                onClick={handleSubmit}
                disabled={!canProceed() || isProcessing}
                className="w-full bg-blue-600 hover:bg-blue-700 py-4 text-lg font-bold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? (
                  <div className="flex items-center justify-center gap-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    <span>Creating your account...</span>
                  </div>
                ) : (
                  <>
                    {noFreeTrial ? 'Start Subscription' : 'Start 3-Day Free Trial'}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>

              {isProcessing && (
                <div className="text-sm text-gray-600 text-center mt-2 animate-pulse">
                  Please wait, this may take a few seconds...
                </div>
              )}

              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-xs text-green-800 text-center font-medium">
                  No credit card required • 3-day free access • Cancel anytime
                </p>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-800 text-center">
                  <span className="font-medium">How we use this info:</span> Our AI analyzes your product details to find ideal prospects, 
                  craft personalized emails, and launch campaigns that book meetings automatically.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Preview & Social Proof */}
        <div className="space-y-6">
          {/* Campaign Preview */}
          {formData.productName && formData.targetAudience && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-6 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg"
            >
              <h4 className="font-semibold text-gray-900 mb-4">
                Your AI Campaign Preview
              </h4>
              
              <div className="space-y-4">
                <div>
                  <div className="text-sm text-gray-600 mb-1">Estimated Reach</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {formData.estimatedReach.toLocaleString()}+ prospects
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-600 mb-2">AI-Generated Subject Lines</div>
                  <ul className="space-y-1">
                    {formData.sampleSubjectLines.map((subject, i) => (
                      <li key={i} className="text-sm bg-white p-2 rounded">
                        "{subject}"
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <div className="text-sm text-gray-600 mb-2">Target Companies Include</div>
                  <div className="flex flex-wrap gap-2">
                    {formData.targetCompanies.map((company, i) => (
                      <Badge key={i} variant="secondary">{company}</Badge>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Testimonials */}
          <div className="space-y-4">
            <h4 className="font-semibold text-gray-900">What {formData.targetAudience || 'founders'} are saying</h4>
            {getTestimonials(formData.targetAudience).map((testimonial, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }}
                className="p-4 bg-white border border-gray-200 rounded-lg"
              >
                <div className="flex gap-1 mb-2">
                  {[...Array(testimonial.rating)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-sm text-gray-700 mb-2">"{testimonial.content}"</p>
                <div className="text-xs text-gray-500">
                  <span className="font-medium">{testimonial.name}</span> • {testimonial.role}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Trust Badges */}
          <div className="flex items-center justify-center gap-4 pt-4">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Shield className="w-4 h-4" />
              <span>SSL Secure</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <CreditCard className="w-4 h-4" />
              <span>Powered by Whop</span>
            </div>
          </div>
          <div className="text-center text-xs text-gray-600">
            You'll be redirected to Whop for secure payment.
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            {noFreeTrial 
              ? 'Get Started - Book Your First Meeting in 24 Hours'
              : 'Start Your 3-Day Free Trial - Book Your First Meeting in 24 Hours'}
          </DialogTitle>
        </DialogHeader>
        
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};

export default OptimizedOnboardingV2; 